// Dashboard Oficial — reorganizado en 3 niveles funcionales.
// Sprint 5 · Paso 5:
//   Nivel 1 · Acción Hoy         — casos que requieren atención inmediata.
//   Nivel 2 · Indicadores navegables — KPIs, matriz, alertas, cursos foco.
//   Nivel 3 · Explorador operativo — pestañas Prioridades/Observaciones/Casos.
// Mantiene matriz preventiva, alertas y separación Oficial/Inducción.
// No expone score crudo (los niveles Verde/Amarillo/Naranjo/Rojo son etiquetas).
import React, { useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Zap, BarChart3, Compass } from 'lucide-react';
import KpiCards from './KpiCards';
import ActionTodayPanel from './ActionTodayPanel';
import DimensionDistribution from './DimensionDistribution';
import FocusCourses from './FocusCourses';
import OpenCasesGrid from './OpenCasesGrid';
import { SentinelEventsPanel, OverdueCasesPanel } from './SentinelAndOverdue';
import ExploradorOperativo from './ExploradorOperativo';
import { track } from '../../../lib/metrics';
import {
  useKpis, useAccionHoy, useDistribucionDimensiones,
  useCursosFoco, useCasosAbiertos, useCasosVencidos,
} from '../../../lib/dashboardQueries';

const LevelHeader = ({ n, title, subtitle, icon: Icon }) => (
  <div className="flex items-start gap-3 mb-3" data-testid={`nivel-${n}-header`}>
    <div className="w-9 h-9 rounded-xl bg-[#EEF0FE] text-[#5346E6] flex items-center justify-center shrink-0 font-extrabold text-sm">
      {Icon ? <Icon className="w-4 h-4" /> : n}
    </div>
    <div>
      <p className="text-[10px] font-bold uppercase tracking-widest text-[#5346E6]">
        Nivel {n}
      </p>
      <h2 className="text-lg font-extrabold text-[#0B1B5E] leading-tight">{title}</h2>
      {subtitle && <p className="text-xs text-[#475472] mt-0.5">{subtitle}</p>}
    </div>
  </div>
);

export default function DashboardOficial() {
  // Layout ya es la fuente única de eventos-centinela (H-F2).
  const outlet = useOutletContext() || {};
  const { eventosCentinela = [], eventosLoading: sentinelaLoading = false } = outlet;

  const qKpis        = useKpis();
  const qAccion      = useAccionHoy();
  const qDimensiones = useDistribucionDimensiones(30);
  const qCursos      = useCursosFoco(8);
  const qAbiertos    = useCasosAbiertos();
  const qVencidos    = useCasosVencidos();

  // Sprint 5 · métricas: dashboard_view + load_time cuando los KPIs terminan de cargar.
  const loadReportedRef = useRef(false);
  useEffect(() => {
    if (!qKpis.isLoading && !loadReportedRef.current) {
      loadReportedRef.current = true;
      track('dashboard_view');
      track('load_time', { view: 'dashboard_oficial', load_ms: Math.round(performance.now()) });
    }
  }, [qKpis.isLoading]);

  const forbidden = qKpis.error?.status === 403 && !qKpis.data;
  if (forbidden) {
    return (
      <div className="bg-white rounded-2xl border border-[#FADBD8] p-6 text-sm text-[#8A1616]" data-testid="dashboard-forbidden">
        Tu rol no tiene acceso al dashboard institucional.
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-oficial">
      {/* ==================== NIVEL 1 · ACCIÓN HOY ==================== */}
      <section data-testid="nivel-1">
        <LevelHeader
          n={1}
          icon={Zap}
          title="Acción hoy"
          subtitle="Casos que requieren tu atención inmediata: sin primera acción, con revisión vencida, evento centinela activo o en carril crisis."
        />
        <ActionTodayPanel casos={qAccion.data?.casos || []} loading={qAccion.isLoading} />
      </section>

      {/* ==================== NIVEL 2 · INDICADORES ==================== */}
      <section data-testid="nivel-2">
        <LevelHeader
          n={2}
          icon={BarChart3}
          title="Indicadores navegables"
          subtitle="Vista general del clima preventivo. Cada tarjeta lleva al detalle correspondiente."
        />
        <div className="space-y-5">
          <KpiCards data={qKpis.data || null} loading={qKpis.isLoading} />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 space-y-5">
              <OpenCasesGrid
                grid={qAbiertos.data?.grid || []}
                total={qAbiertos.data?.total || 0}
                loading={qAbiertos.isLoading}
              />
              <DimensionDistribution
                dimensiones={qDimensiones.data?.dimensiones || []}
                loading={qDimensiones.isLoading}
              />
            </div>
            <div className="space-y-5">
              <SentinelEventsPanel casos={eventosCentinela} loading={sentinelaLoading} />
              <OverdueCasesPanel casos={qVencidos.data?.casos || []} loading={qVencidos.isLoading} />
              <FocusCourses cursos={qCursos.data?.cursos || []} loading={qCursos.isLoading} />
            </div>
          </div>
        </div>
      </section>

      {/* ==================== NIVEL 3 · EXPLORADOR ==================== */}
      <section data-testid="nivel-3">
        <LevelHeader
          n={3}
          icon={Compass}
          title="Explorador operativo"
          subtitle="Recorre las prioridades del día, revisa observaciones o consulta el listado completo de casos."
        />
        <ExploradorOperativo />
      </section>
    </div>
  );
}
